/**
 * Dashboard API functions — v4 client
 * canonical: S0 — lines 578–721
 *
 * Auth delegated to shared apiFetch helper in ./client.
 * All functions return typed data or throw on HTTP/network error.
 */

import { apiFetch } from './client'
import type {
  DashboardStats,
  Project,
  FollowUpQueueResponse,
  RetentionAlertsResponse,
  LienAlertsResponse,
  AskAriaRequest,
  AskAriaResponse,
} from './types'

// ── Endpoint functions ─────────────────────────────────────────────────────

/**
 * KPI aggregates for the current user.
 * GET /api/reports/stats → {ok, data: DashboardStats}
 * canonical: lines 608–629
 */
export async function fetchDashboardStats(): Promise<DashboardStats> {
  const res = await apiFetch<{ ok: boolean; data: DashboardStats }>('GET', '/api/reports/stats')
  return res.data
}

/**
 * Full project list with per-project billing aggregates.
 * GET /api/projects → Project[] (flat array)
 * canonical: lines 631–665
 */
export async function fetchProjects(): Promise<Project[]> {
  return apiFetch<Project[]>('GET', '/api/projects')
}

/**
 * ARIA follow-up queue — overdue pay apps with urgency tone.
 * GET /api/aria/follow-up-queue
 * canonical: lines 677–691 (Active alerts block)
 */
export async function fetchFollowUpQueue(): Promise<FollowUpQueueResponse> {
  return apiFetch<FollowUpQueueResponse>('GET', '/api/aria/follow-up-queue')
}

/**
 * Submitted pay apps with line RET % below project default.
 * GET /api/aria/retention-alerts
 */
export async function fetchRetentionAlerts(): Promise<RetentionAlertsResponse> {
  return apiFetch<RetentionAlertsResponse>('GET', '/api/aria/retention-alerts')
}

/**
 * Lien alerts — used for both Pre Lien tracker and Lien deadlines blocks.
 * GET /api/aria/lien-alerts
 * canonical: lines 692–710
 */
export async function fetchLienAlerts(): Promise<LienAlertsResponse> {
  return apiFetch<LienAlertsResponse>('GET', '/api/aria/lien-alerts')
}

/**
 * Ask ARIA — single-turn chat with conversation persistence.
 * POST /api/ai/ask  body: {question}  → {answer, questionType}
 * canonical: lines 711–714
 */
export async function askAria(req: AskAriaRequest): Promise<AskAriaResponse> {
  return apiFetch<AskAriaResponse>('POST', '/api/ai/ask', req)
}
